
export const encryptSymmetric = async (plaintext: string, key: string) => {
    // create a random 96-bit initialization vector (IV)
    const iv = crypto.getRandomValues(new Uint8Array(12)).buffer;
  
    // encode the text you want to encrypt
    const encodedPlaintext = new TextEncoder().encode(plaintext);
  
    // prepare the secret key for encryption
    const secretKey = await crypto.subtle.importKey('raw', base64StringToArrayBuffer(key), {
        name: 'AES-GCM',
        length: 256
    }, true, ['encrypt', 'decrypt']);
  
    // encrypt the text with the secret key
    const ciphertext = await crypto.subtle.encrypt({
        name: 'AES-GCM',
        iv
    }, secretKey, encodedPlaintext);
    
    // return the encrypted text "ciphertext" and the IV encoded in base64
    return ({
        ciphertext: arrayBufferToBase64String(ciphertext),
        iv: arrayBufferToBase64String(iv)
    });
}

export const decryptSymmetric = async (ciphertext: string, iv: string, key: string) => {
    // prepare the secret key
    const secretKey = await crypto.subtle.importKey(
        'raw',
        base64StringToArrayBuffer(key), 
        {
        name: 'AES-GCM',
        length: 256
    }, true, ['encrypt', 'decrypt']);
  
    // decrypt the encrypted text "ciphertext" with the secret key and IV
    const cleartext = await crypto.subtle.decrypt({
        name: 'AES-GCM',
        iv: base64StringToArrayBuffer(iv),
    }, secretKey, base64StringToArrayBuffer(ciphertext));
  
    // decode the text and return it
    return new TextDecoder().decode(cleartext);
}

const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

// Use a lookup table to find the index.
const lookup = typeof Uint8Array === 'undefined' ? [] : new Uint8Array(256);
for (let i = 0; i < chars.length; i++) {
    lookup[chars.charCodeAt(i)] = i;
}

export const arrayBufferToBase64String = (arraybuffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(arraybuffer),
          len = bytes.length;
    let base64 = '';

    for (let i = 0; i < len; i += 3) {
        base64 += chars[bytes[i] >> 2];
        base64 += chars[((bytes[i] & 3) << 4) | (bytes[i + 1] >> 4)];
        base64 += chars[((bytes[i + 1] & 15) << 2) | (bytes[i + 2] >> 6)];
        base64 += chars[bytes[i + 2] & 63];
    }

    if (len % 3 === 2) {
        base64 = base64.substring(0, base64.length - 1) + '=';
    } else if (len % 3 === 1) {
        base64 = base64.substring(0, base64.length - 2) + '==';
    }

    return base64;
};

const base64StringToArrayBuffer = (base64: string): ArrayBuffer => {
    const len = base64.length;
    let bufferLength = base64.length * 0.75,
        i,
        p = 0,
        encoded1,
        encoded2,
        encoded3,
        encoded4;

    if (base64[base64.length - 1] === '=') {
        bufferLength--;
        if (base64[base64.length - 2] === '=') {
            bufferLength--;
        }
    }

    const arraybuffer = new ArrayBuffer(bufferLength),
        bytes = new Uint8Array(arraybuffer);

    for (i = 0; i < len; i += 4) {
        encoded1 = lookup[base64.charCodeAt(i)];
        encoded2 = lookup[base64.charCodeAt(i + 1)];
        encoded3 = lookup[base64.charCodeAt(i + 2)];
        encoded4 = lookup[base64.charCodeAt(i + 3)];

        bytes[p++] = (encoded1 << 2) | (encoded2 >> 4);
        bytes[p++] = ((encoded2 & 15) << 4) | (encoded3 >> 2);
        bytes[p++] = ((encoded3 & 3) << 6) | (encoded4 & 63);
    }

    return arraybuffer;
};
